import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useDropdowns } from "@/contexts/DropdownContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SortableHeader, TablePagination } from "@/components/TableComponents";
import { useTableSort } from "@/hooks/useTableSort";
import { Plus, Edit, Trash2, Search, ExternalLink, FolderKanban } from "lucide-react";
import { toast } from "sonner";
import UserSearchDropdown from "@/components/UserSearchDropdown";
import { useFormDraft } from "@/hooks/useFormDraft";
import { DraftRestoredBanner } from "@/components/DraftRestoredBanner";
import { DraftIndicatorBadge } from "@/components/DraftIndicatorBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export default function ProjectsPageNew() {
  const navigate = useNavigate();
  const { token, hasPermission } = useAuth();
  const { getOptions, getColor } = useDropdowns();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterModel, setFilterModel] = useState("all");

  // Get dropdown options
  const statusOptions = getOptions("projects", "status");
  const typeOptions = getOptions("projects", "type");
  const executionModelOptions = getOptions("projects", "execution_model");
  
  const projectFormDefaults = {
    id: "", // Optional manual ID
    name: "",
    client: "",
    type: "Retail",
    execution_model: "Turnkey",
    city: "",
    state: "",
    area: "",
    units: "", // New field for area units
    location_url: "", // New field for Google Maps URL
    start_date: "",
    target_date: "",
    pm: "",
    pm_id: "", // Project Manager user ID
    status: "Planning",
    total_budget: "",
    remarks: "",
  };

  const [formData, setFormData, draft] = useFormDraft(
    "newProjectForm",
    projectFormDefaults,
    { enabled: dialogOpen && !editingProject }
  );
  const [draftRestored, setDraftRestored] = useState(false);

  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";

  useEffect(() => {
    fetchProjects();
    fetchUsers();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      toast.error("Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/users/assignable`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        total_budget: formData.total_budget ? parseFloat(formData.total_budget) : null,
        id: formData.id || undefined // Only send if provided
      };
      // Remove empty id for update
      if (editingProject) {
        delete submitData.id;
        await axios.put(`${API}/projects/${editingProject.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Project updated successfully");
      } else {
        await axios.post(`${API}/projects`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Project created successfully");
        draft.clear();
      }
      setDialogOpen(false);
      setDraftRestored(false);
      resetForm();
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    try {
      await axios.delete(`${API}/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Project deleted successfully");
      fetchProjects();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const resetForm = () => {
    setFormData(projectFormDefaults);
    setEditingProject(null);
  };

  const openEditDialog = (project) => {
    setEditingProject(project);
    setFormData({
      id: project.id,
      name: project.name,
      client: project.client,
      type: project.type,
      execution_model: project.execution_model,
      city: project.city,
      state: project.state,
      area: project.area,
      units: project.units || "",
      location_url: project.location_url || "",
      start_date: project.start_date,
      target_date: project.target_date,
      pm: project.pm,
      pm_id: project.pm_id || "",
      status: project.status,
      total_budget: project.total_budget || "",
      remarks: project.remarks || "",
    });
    setDialogOpen(true);
  };

  const canWrite = hasPermission("projects:write");
  const canDelete = hasPermission("projects:delete");

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.pm.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || project.status === filterStatus;
    const matchesType = filterType === "all" || project.type === filterType;
    const matchesModel = filterModel === "all" || project.execution_model === filterModel;
    return matchesSearch && matchesStatus && matchesType && matchesModel;
  });

  // Sorting and pagination
  const {
    sortKey,
    sortOrder,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    startIndex,
    endIndex,
    paginatedData,
    totalItems
  } = useTableSort(filteredProjects, {
    defaultSortKey: "created_at",
    defaultSortOrder: "desc",
    defaultPageSize: 10
  });

  const hasActiveFilters = filterStatus !== "all" || filterType !== "all" || filterModel !== "all";
  const clearFilters = () => {
    setFilterStatus("all");
    setFilterType("all");
    setFilterModel("all");
  };

  if (loading) return <div className="p-8 bg-slate-50 dark:bg-slate-900 min-h-screen text-slate-600 dark:text-slate-400">Loading...</div>;

  return (
    <div className="p-8 bg-slate-50 dark:bg-slate-900 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
            Project Master
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Manage all construction and interior projects
          </p>
        </div>
        {canWrite && (
          <div className="flex items-center gap-3">
            <DraftIndicatorBadge
              storageKey="newProjectForm"
              onResume={() => {
                const restored = draft.restoreIfAny();
                setDraftRestored(restored);
                setDialogOpen(true);
              }}
            />
            <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (open) {
              // Opening: if creating (not editing), try restoring a saved draft
              if (!editingProject) {
                const restored = draft.restoreIfAny();
                setDraftRestored(restored);
              }
            } else {
              // Closing: keep any pending draft (don't clear on backdrop click)
              setDraftRestored(false);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-project-button"
                className="bg-brillien-600 hover:bg-brillien-700 text-white rounded-lg h-10 px-4 font-medium"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto dark:bg-slate-800 dark:border-slate-700">
              <DialogHeader>
                <DialogTitle>{editingProject ? "Edit Project" : "Add New Project"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {!editingProject && draftRestored && (
                  <DraftRestoredBanner
                    onDiscard={() => {
                      draft.clear();
                      setDraftRestored(false);
                    }}
                  />
                )}
                {/* Manual ID field - only show when creating new project */}
                {!editingProject && (
                  <div className="bg-slate-50 dark:bg-slate-700/50 p-3 rounded-lg border border-dashed border-slate-300 dark:border-slate-400">
                    <Label htmlFor="manual_id" className="text-sm font-medium">
                      Project ID <span className="text-slate-400 font-normal">(Optional - leave blank for auto-generated ID)</span>
                    </Label>
                    <Input
                      id="manual_id"
                      value={formData.id}
                      onChange={(e) => setFormData({...formData, id: e.target.value})}
                      placeholder="e.g., PRJ-2025-001"
                      className="rounded-sm mt-1"
                      data-testid="project-manual-id"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Project Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="client">Client *</Label>
                    <Input
                      id="client"
                      value={formData.client}
                      onChange={(e) => setFormData({...formData, client: e.target.value})}
                      required
                      className="rounded-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="type">Project Type *</Label>
                    <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {typeOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="execution_model">Execution Model *</Label>
                    <Select value={formData.execution_model} onValueChange={(value) => setFormData({...formData, execution_model: value})}>
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {executionModelOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      required
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      required
                      className="rounded-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="area">Area *</Label>
                    <Input
                      id="area"
                      value={formData.area}
                      onChange={(e) => setFormData({...formData, area: e.target.value})}
                      placeholder="e.g., 5,500"
                      required
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="units">Units</Label>
                    <Input
                      id="units"
                      value={formData.units}
                      onChange={(e) => setFormData({...formData, units: e.target.value})}
                      placeholder="e.g., sq.ft, sq.m"
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="total_budget">Total Budget (₹)</Label>
                    <Input
                      id="total_budget"
                      type="number"
                      value={formData.total_budget}
                      onChange={(e) => setFormData({...formData, total_budget: e.target.value})}
                      placeholder="e.g., 5000000"
                      className="rounded-sm"
                      data-testid="project-total-budget"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="location_url">Location (Google Maps URL)</Label>
                  <Input
                    id="location_url"
                    value={formData.location_url}
                    onChange={(e) => setFormData({...formData, location_url: e.target.value})}
                    placeholder="Paste Google Maps link here..."
                    className="rounded-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_date">Start Date *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      required
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="target_date">Target Completion Date *</Label>
                    <Input
                      id="target_date"
                      type="date"
                      value={formData.target_date}
                      onChange={(e) => setFormData({...formData, target_date: e.target.value})}
                      required
                      className="rounded-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pm">Project Manager *</Label>
                    <UserSearchDropdown
                      users={users}
                      value={formData.pm_id}
                      selectedLabel={formData.pm}
                      onChange={(id, name) => setFormData({...formData, pm_id: id, pm: name})}
                      placeholder="Search by name or email..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="status">Status *</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="remarks">Remarks</Label>
                  <Input
                    id="remarks"
                    value={formData.remarks || ''}
                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    placeholder="Any additional notes about this project (visible in the project list)"
                    className="rounded-sm"
                    data-testid="project-remarks-input"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-brillien-600 hover:bg-brillien-700 rounded-lg">
                    {editingProject ? "Update" : "Create"} Project
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Search and Filters */}
      <Card className="p-4 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg mb-6">
        <div className="flex flex-col gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search projects by name, client, ID, city, or PM..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="projects-search-input"
              className="pl-10 h-10 rounded-lg border-slate-300 dark:border-slate-400 dark:bg-slate-700 dark:text-white"
            />
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Filters:</span>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36 h-9 rounded-lg" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statusOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-40 h-9 rounded-lg" data-testid="filter-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {typeOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterModel} onValueChange={setFilterModel}>
              <SelectTrigger className="w-36 h-9 rounded-lg" data-testid="filter-model">
                <SelectValue placeholder="Model" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Models</SelectItem>
                {executionModelOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 dark:text-slate-400 h-9">
                Clear All
              </Button>
            )}
            <span className="text-sm text-slate-500 dark:text-slate-400 ml-auto">
              {filteredProjects.length} of {projects.length} projects
            </span>
          </div>
        </div>
      </Card>

      <Card className="border-slate-300 dark:border-slate-400 dark:bg-slate-800 rounded-lg overflow-hidden">
        <Table className="border-collapse">
          <TableHeader>
            <TableRow className="bg-slate-100 dark:bg-slate-700/50 border-b-2 border-slate-400 dark:border-slate-400">
              <TableHead className="border-r border-slate-300 dark:border-slate-400">
                <SortableHeader sortKey="id" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Project ID
                </SortableHeader>
              </TableHead>
              <TableHead className="border-r border-slate-300 dark:border-slate-400">
                <SortableHeader sortKey="name" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Name / Client
                </SortableHeader>
              </TableHead>
              <TableHead className="border-r border-slate-300 dark:border-slate-400">
                <SortableHeader sortKey="city" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Location
                </SortableHeader>
              </TableHead>
              <TableHead className="border-r border-slate-300 dark:border-slate-400">
                <SortableHeader sortKey="type" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Type
                </SortableHeader>
              </TableHead>
              <TableHead className="border-r border-slate-300 dark:border-slate-400">
                <SortableHeader sortKey="pm" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  PM
                </SortableHeader>
              </TableHead>
              <TableHead className="border-r border-slate-300 dark:border-slate-400">
                <SortableHeader sortKey="completion" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Progress
                </SortableHeader>
              </TableHead>
              <TableHead className="border-r border-slate-300 dark:border-slate-400">
                <SortableHeader sortKey="status" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Status
                </SortableHeader>
              </TableHead>
              <TableHead className="border-r border-slate-300 dark:border-slate-400">
                <SortableHeader sortKey="created_at" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Created
                </SortableHeader>
              </TableHead>
              <TableHead className="border-r border-slate-300 dark:border-slate-400">
                <SortableHeader sortKey="updated_at" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Updated
                </SortableHeader>
              </TableHead>
              <TableHead className="border-r border-slate-300 dark:border-slate-400 font-semibold text-slate-700 dark:text-slate-200">
                Remarks
              </TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedData.map((project) => (
              <TableRow 
                key={project.id} 
                className="hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer border-b border-slate-300 dark:border-slate-400" 
                data-testid={`project-row-${project.id}`}
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                <TableCell className="font-mono text-sm text-slate-600 dark:text-slate-400 border-r border-slate-300 dark:border-slate-400">{project.id}</TableCell>
                <TableCell className="border-r border-slate-300 dark:border-slate-400">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">{project.name}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{project.client}</p>
                  </div>
                </TableCell>
                <TableCell className="border-r border-slate-300 dark:border-slate-400">
                  <div className="text-sm">
                    <p className="text-slate-700 dark:text-slate-300">{project.city}, {project.state}</p>
                    <p className="text-slate-500 dark:text-slate-400">{project.area}</p>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-slate-700 dark:text-slate-300 border-r border-slate-300 dark:border-slate-400">{project.type}</TableCell>
                <TableCell className="text-sm text-slate-700 dark:text-slate-300 border-r border-slate-300 dark:border-slate-400">{project.pm}</TableCell>
                <TableCell className="border-r border-slate-300 dark:border-slate-400">
                  <div className="w-20">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{project.completion}%</span>
                    </div>
                    <Progress value={project.completion} className="h-1.5" />
                  </div>
                </TableCell>
                <TableCell className="border-r border-slate-300 dark:border-slate-400">
                  <Badge className={`${getColor("projects", "status", project.status)} border text-xs`}>{project.status}</Badge>
                </TableCell>
                <TableCell className="text-sm text-slate-600 dark:text-slate-400 border-r border-slate-300 dark:border-slate-400">{formatDate(project.created_at)}</TableCell>
                <TableCell className="text-sm text-slate-600 dark:text-slate-400 border-r border-slate-300 dark:border-slate-400">{formatDate(project.updated_at)}</TableCell>
                <TableCell
                  className="text-sm text-slate-700 dark:text-slate-300 border-r border-slate-300 dark:border-slate-400 max-w-[220px]"
                  title={project.remarks || ''}
                  data-testid={`project-remarks-${project.id}`}
                >
                  <span className="line-clamp-2 whitespace-normal break-words">
                    {project.remarks || <span className="text-slate-400">—</span>}
                  </span>
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => navigate(`/projects/${project.id}`)} 
                      className="h-8 w-8 p-0"
                      data-testid={`view-project-${project.id}`}
                    >
                      <ExternalLink className="h-4 w-4 text-brillien-600" />
                    </Button>
                    {canWrite && (
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(project)} className="h-8 w-8 p-0">
                        <Edit className="h-4 w-4 text-slate-600" />
                      </Button>
                    )}
                    {isAdmin && canDelete && (
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(project.id)} className="h-8 w-8 p-0" data-testid={`delete-project-${project.id}`}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {paginatedData.length === 0 && (
          <div className="text-center py-12 text-slate-500 dark:text-slate-400">
            <FolderKanban className="h-12 w-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
            <p>No projects found</p>
            {canWrite && <p className="text-sm mt-1">Click &quot;Add New Project&quot; to create one</p>}
          </div>
        )}
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          startIndex={startIndex}
          endIndex={endIndex}
          totalItems={totalItems}
        />
      </Card>
    </div>
  );
}
